import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "components/ui/collapsible";
import { Separator } from "components/ui/separator";
import { cn } from "@/lib/utils";
import * as QueryKeys from "data";
import type { DeploymentInfo } from "data/models";
import { fetchDeploymentStatus } from "data/queries-common";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import Draggable from "react-draggable";
import { useQuery } from "@tanstack/react-query";

dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);

const statusBadgeClass = (isUpToDate: boolean) =>
  cn(
    "font-mono",
    isUpToDate
      ? "bg-green-600 text-white hover:bg-green-600"
      : "bg-amber-500 text-white hover:bg-amber-500",
  );

const DeploymentCard = ({
  deployment,
  title,
}: {
  deployment: DeploymentInfo;
  title: string;
}) => {
  const isUpToDate = deployment.is_latest_on_branch ?? true;

  return (
    <div className="mb-3">
      <p className="text-muted-foreground mb-1 block text-[0.65rem]">{title}</p>
      <p className="mb-1 font-mono font-semibold">{deployment.branch_name}</p>
      {!isUpToDate && deployment.commits_behind_branch && (
        <p className="mb-1 block text-amber-600 text-xs dark:text-amber-500">
          ⚠️ {deployment.commits_behind_branch} commit
          {deployment.commits_behind_branch > 1 ? "s" : ""} behind
        </p>
      )}
      {isUpToDate && (
        <p className="mb-1 block text-green-600 text-xs dark:text-green-500">
          ✓ Up to date
        </p>
      )}
      <p className="text-muted-foreground text-[0.65rem]">
        Build {deployment.build_number}
        {deployment.commit_hash &&
          ` • ${deployment.commit_hash.substring(0, 7)}`}
      </p>
      {deployment.finish_date && (
        <p className="text-muted-foreground mt-1 block text-[0.65rem]">
          {`Deployed ${dayjs(deployment.finish_date, "YYYYMMDDTHHmmssZ").format(
            "MMMM D, YYYY h:mm A",
          )} (${dayjs(deployment.finish_date, "YYYYMMDDTHHmmssZ").fromNow()})`}
        </p>
      )}
    </div>
  );
};

const DeploymentStatusWidget = () => {
  const [expanded, setExpanded] = useState(false);
  const [minimized, setMinimized] = useState(false);

  // Only show in preprod
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const shouldShow = environment === "preprod";

  const frontendQuery = useQuery({
    queryKey: [QueryKeys.deploymentStatus, "hub_frontend"],
    queryFn: () => fetchDeploymentStatus("hub_frontend"),
    enabled: shouldShow,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 4 * 60 * 1000,
  });

  const backendQuery = useQuery({
    queryKey: [QueryKeys.deploymentStatus, "hub_backend_api"],
    queryFn: () => fetchDeploymentStatus("hub_backend_api"),
    enabled: shouldShow,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 4 * 60 * 1000,
  });

  if (!shouldShow) return null;
  if (frontendQuery.isPending || backendQuery.isPending) return null;
  if (frontendQuery.isError || backendQuery.isError) return null;

  const frontendInfo = frontendQuery.data;
  const backendInfo = backendQuery.data;

  if (!frontendInfo || !backendInfo) return null;

  const anyOutdated =
    !(frontendInfo.is_latest_on_branch ?? true) ||
    !(backendInfo.is_latest_on_branch ?? true);

  if (minimized) {
    return (
      <Draggable bounds="body" handle=".drag-handle">
        <div
          className="bg-card ring-foreground/10 fixed right-4 bottom-4 z-[9999] cursor-pointer rounded-lg shadow-md ring-1"
          onClick={() => setMinimized(false)}
        >
          <div className="drag-handle p-1">
            <Badge className={statusBadgeClass(!anyOutdated)}>Hub</Badge>
          </div>
        </div>
      </Draggable>
    );
  }

  return (
    <Draggable bounds="body" handle=".drag-handle">
      <div className="bg-card ring-foreground/10 fixed right-4 bottom-4 z-[9999] min-w-[250px] max-w-[400px] rounded-lg shadow-md ring-1">
        <Collapsible open={expanded} onOpenChange={setExpanded}>
          <div className="drag-handle grid cursor-move grid-cols-[auto_1fr_auto_auto] items-center gap-2 p-2">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="col-span-3 grid grid-cols-[auto_1fr_auto] items-center gap-2 text-left cursor-pointer"
              >
                <span className="text-muted-foreground text-[0.65rem]">
                  FE:
                </span>
                <Badge
                  className={statusBadgeClass(
                    frontendInfo.is_latest_on_branch ?? true,
                  )}
                >
                  {frontendInfo.branch_name}
                </Badge>
                <span className="row-span-2 flex items-center">
                  {expanded ? (
                    <ChevronUp className="size-4" />
                  ) : (
                    <ChevronDown className="size-4" />
                  )}
                </span>
                <span className="text-muted-foreground text-[0.65rem]">
                  BE:
                </span>
                <Badge
                  className={statusBadgeClass(
                    backendInfo.is_latest_on_branch ?? true,
                  )}
                >
                  {backendInfo.branch_name}
                </Badge>
              </button>
            </CollapsibleTrigger>
            <Button
              variant="ghost"
              size="icon"
              className="row-span-2 size-6"
              onClick={(e) => {
                e.stopPropagation();
                setMinimized(true);
              }}
              aria-label="Minimize"
              title="Minimize"
            >
              −
            </Button>
          </div>

          <CollapsibleContent>
            <div className="border-t p-4 pt-0">
              <DeploymentCard deployment={frontendInfo} title="Frontend" />
              <Separator className="my-2" />
              <DeploymentCard deployment={backendInfo} title="Backend API" />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </Draggable>
  );
};

export default DeploymentStatusWidget;
